import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Layout, Space, Typography } from 'antd';
import { Link, NavLink, Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DeleteOutlined, GroupOutlined } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import localStyles from 'src/components/pages/Company/CompanyPage/CompanyPage.module.css';
import RouteProtected from 'src/components/blocks/RouteProtected/RouteProtected';
import InputEditableField from 'src/components/fields/InputEditableField';
import SwitchSelectField from 'src/components/fields/SwitchSelectField';
import { FormattedMessage } from 'react-intl';
import { formatDatetime } from 'src/helpers/formatters';
import Loader from 'src/components/atoms/Loader';
import { useTemplateById } from 'src/features/templates/templates.hooks';
import { deleteTemplate, fetchTemplate, updateTemplate } from 'src/features/templates/templates.state';
import { ContentTemplate } from 'src/features/templates/templates.types';
import ContentTemplateCollectionsTab from 'src/components/pages/Content/ContentTemplatePage/ContentTemplateCollectionsTab';
import { exportTemplateAPI, publishTemplateAPI } from 'src/features/templates/templates.api';
import ContentTemplateCompaniesTab from 'src/components/pages/Content/ContentTemplatePage/ContentTemplateCompaniesTab';
import ContentTemplateOfflineCompaniesTab from 'src/components/pages/Content/ContentTemplatePage/ContentTemplateOfflineCompaniesTab/ContentTemplateOfflineCompaniesTab';
import ContentTemplateDeviceGroupsTab from 'src/components/pages/Content/ContentTemplatePage/ContentTemplateDeviceGroupsTab';

const { Content } = Layout;

const ContentTemplatePage: React.FC = () => {
  const { templateId } = useParams() as { templateId: string };
  const dispatch: AppDispatch = useDispatch();
  const template = useTemplateById(templateId) as ContentTemplate;
  const history = useHistory();
  const [publishing, setPublishing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleDeleteButtonClick = useCallback(async () => {
    const response = await dispatch(deleteTemplate({ templateId }));
    if (deleteTemplate.fulfilled.match(response)) {
      history.push(`/content/collections`);
    }
  }, [dispatch, templateId, history]);
  const handleUpdate = useCallback(
    (data: Partial<ContentTemplate>) => {
      dispatch(updateTemplate({ templateId, data }));
    },
    [templateId, dispatch]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    const result = await exportTemplateAPI({ templateId });
    console.log(result.data);
    setExporting(false);
  }, [templateId]);

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    await publishTemplateAPI({ templateId });
    setPublishing(false);
  }, [templateId]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/content/templates'>
                <Space>
                  <GroupOutlined />
                  <FormattedMessage id={'view.content.template.title'} />
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/content/templates/${templateId}`}>{template.name}</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Space direction={'vertical'} size={16}>
            <InputEditableField level={2} value={template.name} onBlur={e => handleUpdate({ name: e })} />
            <Space size='small' wrap>
              <NavLink to={`collections`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <GroupOutlined />
                  <span>
                    <FormattedMessage id={'view.content.template.tab.collections.title'} />
                  </span>
                </Button>
              </NavLink>
              <NavLink to={`companies`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <GroupOutlined />
                  <span>
                    <FormattedMessage id={'view.content.template.tab.companies.title'} />
                  </span>
                </Button>
              </NavLink>
              <NavLink to={`offline`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <GroupOutlined />
                  <span>
                    <FormattedMessage id={'view.content.template.tab.companies.title.offline'} />
                  </span>
                </Button>
              </NavLink>
              <NavLink to={`groups`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <GroupOutlined />
                  <span>
                    <FormattedMessage id={'view.content.template.tab.abGroups.title'} />
                  </span>
                </Button>
              </NavLink>
            </Space>
          </Space>
          <Content>
            <Route
              render={({ match: { path } }) => (
                <Switch>
                  <RouteProtected
                    exact
                    path={`/content/templates/:templateId/offline`}
                    component={ContentTemplateOfflineCompaniesTab}
                  />
                  <RouteProtected exact path={`${path}/companies`} component={ContentTemplateCompaniesTab} />
                  <RouteProtected exact path={`${path}/collections`} component={ContentTemplateCollectionsTab} />
                  <RouteProtected exact path={`${path}/groups`} component={ContentTemplateDeviceGroupsTab} />
                  <Redirect from={path} to={`${path}/collections`} exact />
                </Switch>
              )}
            />
          </Content>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type={'primary'} onClick={handlePublish} loading={publishing} disabled={exporting}>
              Опубликовать
            </Button>
            <Button type={'primary'} onClick={handleExport} loading={exporting} disabled={publishing}>
              Скачать JSON
            </Button>
          </Space>
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>
              <FormattedMessage id={'view.content.template.field.disabled'} />
            </Typography.Text>
            <SwitchSelectField value={template.disabled} onSelect={e => handleUpdate({ disabled: e })} />
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.createdAt' />: {formatDatetime(template.createdAt)}
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.updatedAt' />: {formatDatetime(template.updatedAt)}
            </Typography.Text>
            <ConfirmButton type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
              <DeleteOutlined /> <FormattedMessage id={'view.content.template.action.delete'} />
            </ConfirmButton>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const ContentTemplatePageBlock: React.FC = () => {
  const { templateId } = useParams() as { templateId: string };
  const dispatch = useDispatch();
  const template = useTemplateById(templateId);

  useEffect(() => {
    dispatch(fetchTemplate({ templateId }));
  }, [dispatch, templateId]);

  if (!template) {
    return <Loader />;
  }

  return <ContentTemplatePage />;
};
export default ContentTemplatePageBlock;
